/**
 * The investigator's milestones: open while it runs, folded to one line once the verdict
 * lands, and left alone once the reader has opened or closed it.
 */

import { useState } from 'react';
import { Check, ChevronRight, Search } from 'lucide-react';
import { formatDuration } from '@/lib/duration';
import { cn } from '@/lib/utils';
import { Reveal } from '../ui/reveal';
import { StepRail, type RailStep } from '../ui/step-rail';
import type { InvestigationStep } from './review.store';
import { stepDurations, totalElapsed } from './investigation-timing';

const railSteps = (steps: InvestigationStep[], live: boolean): RailStep[] => {
  const durations = stepDurations(steps);
  const rail: RailStep[] = steps.map((step, index) => {
    const running = live && index === steps.length - 1;
    const duration = durations[index];
    return {
      key: `${step.text}-${index}`,
      icon: Check,
      tone: 'text-indigo-600',
      running,
      label: <span className={cn(running && 'font-medium')}>{step.text}</span>,
      meta: !running && duration !== null ? formatDuration(duration) : undefined,
    };
  });

  if (live && rail.length === 0) {
    return [{ key: 'waiting', icon: Search, running: true, label: 'Reading the run’s trace…' }];
  }
  return rail;
};

export const InvestigationSteps = ({ steps, live }: { steps: InvestigationStep[]; live: boolean }) => {
  // Null until the reader takes control; until then the trail follows the run.
  const [userOpen, setUserOpen] = useState<boolean | null>(null);
  const open = userOpen ?? live;

  const total = live ? null : totalElapsed(steps);
  // Open, the list already shows the step it is on, so the header stops repeating it.
  const label = !live
    ? `Investigated · ${steps.length} step${steps.length === 1 ? '' : 's'}`
    : open
      ? 'Investigating'
      : (steps[steps.length - 1]?.text ?? 'Reading the run’s trace…');

  return (
    <div className='flex flex-col'>
      <button type='button' onClick={() => setUserOpen(!open)} className='flex w-full items-center gap-2 text-left'>
        <ChevronRight
          className={cn('size-3.5 shrink-0 text-gray-800 transition-transform duration-300', open && 'rotate-90')}
          aria-hidden
        />
        {live && <span className='size-1.5 shrink-0 animate-pulse rounded-full bg-indigo-500' />}
        <span
          className={cn(
            'min-w-0 flex-1 truncate text-xs leading-4.5',
            live ? 'text-text-body' : 'font-medium text-gray-800'
          )}
        >
          {label}
        </span>
        {total !== null && (
          <span className='shrink-0 text-[10px] text-gray-800 tabular-nums'>{formatDuration(total)}</span>
        )}
      </button>
      <Reveal open={open}>
        <StepRail steps={railSteps(steps, live)} className='pt-1' />
      </Reveal>
    </div>
  );
};
